// ============================================================
// GrowthLane — shared TypeScript types
// ============================================================

export type UserRole = 'district_admin' | 'building_admin' | 'teacher' | 'support' | 'classified'
export type Phase = 'fall' | 'midyear' | 'spring'
export type EvidenceType = 'link' | 'file' | 'photo' | 'note'
export type ReminderChannel = 'email' | 'in_app'

// ── Database row shapes (match Supabase schema) ──────────────

export interface District {
  id: string
  name: string
  slug: string
  domain: string | null
  settings: DistrictSettings
  created_at: string
}

// District-wide evaluation policy, stored as jsonb on districts.
export interface DistrictSettings {
  default_form_template_id?: string
  caseload_target?: number
  auto_remind?: boolean
  require_evidence_distinguished?: boolean
  lock_after_signatures?: boolean
}

export interface School {
  id: string
  district_id: string
  name: string
  created_at: string
}

export interface Profile {
  id: string
  district_id: string
  name: string
  first_name: string
  email: string
  role: UserRole
  school: string | null
  school_id: string | null
  subject: string | null
  years_certified: number
  avatar_seed: number
  notification_prefs: Record<string, boolean>
  created_at: string
  updated_at: string
}

export interface FormTemplate {
  id: string
  district_id: string
  name: string
  description: string | null
  framework_type: string
  is_active: boolean
  structure: FormStructure
  current_version: number
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface FormTemplateVersion {
  id: string
  district_id: string
  template_id: string
  version: number
  structure: FormStructure
  note: string
  published_by: string | null
  published_by_name: string
  published_at: string
}

// The working/published structure of an evaluation form. Stored as jsonb
// on form_templates (working copy) and form_template_versions (immutable).
export interface FormStructure {
  meta?: { role?: string; scale?: string }
  domains?: FormStructureDomain[]
  sections?: FormWorkflowSection[]
}

export interface FormStructureComponent {
  code: string
  title: string
  elements: string[]
  levels: { unsatisfactory: string; basic: string; proficient: string; distinguished: string }
}

export interface FormStructureDomain {
  n: number
  title: string
  components: FormStructureComponent[]
}

export interface FormWorkflowSection {
  key: string
  label: string
  desc: string
  who: 'teacher' | 'evaluator' | 'both'
  on: boolean
  locked?: boolean
}

export interface Assignment {
  id: string
  district_id: string
  teacher_id: string
  evaluator_id: string | null
  form_template_id: string | null
  school_year: string
  created_at: string
  updated_at: string
}

export interface Iplp {
  id: string
  district_id: string
  teacher_id: string
  evaluator_id: string | null
  form_template_id: string | null
  school_year: string
  current_phase: Phase
  due_fall: string | null
  due_midyear: string | null
  due_spring: string | null
  section1: Record<string, unknown>
  section2: Record<string, unknown>
  section3: Record<string, unknown>
  section4: Record<string, unknown>
  section5: Record<string, unknown>
  section6: Record<string, unknown>
  form_version: number
  teacher_signed_at: string | null
  teacher_signed_name: string | null
  admin_signed_at: string | null
  admin_signed_name: string | null
  created_at: string
  updated_at: string
}

export interface ComponentRating {
  id: string
  iplp_id: string
  district_id: string
  component_code: string
  phase: Phase
  self_rating: number | null
  admin_rating: number | null
  self_reflection: string | null
  evaluator_feedback: string | null
  updated_at: string
}

export interface Evidence {
  id: string
  iplp_id: string
  district_id: string
  component_code: string
  type: EvidenceType
  name: string
  url: string | null
  storage_path: string | null
  mime_type: string | null
  size_bytes: number | null
  created_at: string
}

export interface Goal {
  id: string
  iplp_id: string
  district_id: string
  domain_number: number
  building_goal: string
  fall_goal: string
  fall_action_steps: string
  midyear_checkin: string
  spring_reflection: string
  updated_at: string
}

export interface Reminder {
  id: string
  district_id: string
  sender_id: string
  recipient_id: string
  iplp_id: string | null
  channel: ReminderChannel
  message: string
  sent_at: string
}

// ── App-level types (enriched views, UI state) ───────────────

export interface TeacherRecord {
  profile: Profile
  iplp: Iplp | null
  ratings: ComponentRating[]
  evidence: Evidence[]
  goals: Goal[]
  assignment: Assignment | null
  evaluator: Profile | null
  completion: { fall: number; midyear: number; spring: number }
}

export type NavState =
  | { page: 'overview' }
  | { page: 'summary' }
  | { page: 'goals' }
  | { page: 'sections' }
  | { page: 'history' }
  | { page: 'domain'; domain: number; code: string }
  | { page: 'dashboard' }
  | { page: 'forms' }
  | { page: 'assign' }

export type SettingsTab = 'profile' | 'notifications' | 'security' | 'district'

export interface AppState {
  role: 'teacher' | 'admin'
  phase: Phase
  nav: NavState
  reviewId: string | null
}

// ── Danielson framework static types ─────────────────────────

export interface FrameworkComponent {
  code: string
  title: string
  elements: string[]
  levels: {
    unsatisfactory: string
    basic: string
    proficient: string
    distinguished: string
  }
  evidenceSeed: string
}

export interface FrameworkDomain {
  n: number
  title: string
  components: FrameworkComponent[]
}

// ── Convenience helpers ──────────────────────────────────────

export const LEVEL_NAMES: Record<number, string> = {
  1: 'Unsatisfactory',
  2: 'Basic',
  3: 'Proficient',
  4: 'Distinguished',
}

export const PHASE_INFO: Record<Phase, { label: string; short: string; color: string; due: string }> = {
  fall:    { label: 'Fall Self-Reflection',  short: 'Fall',     color: '#b3823a', due: 'Oct 15, 2025' },
  midyear: { label: 'Midyear Check-In',      short: 'Midyear',  color: '#2f6f82', due: 'Feb 6, 2026'  },
  spring:  { label: 'Spring Summative',      short: 'Spring',   color: '#4a8a60', due: 'Mar 6, 2026'  },
}

// ── School years (multi-year navigation + history) ───────────
export type YearStatus = 'active' | 'planning' | 'closed'

export interface SchoolYearInfo {
  value: string   // DB school_year value, e.g. '2025-2026'
  label: string   // display, e.g. '2025–26'
  status: YearStatus
}

// Ordered newest → oldest. The "active" year is the live evaluation cycle.
export const SCHOOL_YEARS: SchoolYearInfo[] = [
  { value: '2026-2027', label: '2026–27', status: 'planning' },
  { value: '2025-2026', label: '2025–26', status: 'active' },
  { value: '2024-2025', label: '2024–25', status: 'closed' },
  { value: '2023-2024', label: '2023–24', status: 'closed' },
]

export const ACTIVE_YEAR =
  SCHOOL_YEARS.find((y) => y.status === 'active')?.value ?? '2025-2026'

export function yearInfo(value: string): SchoolYearInfo {
  return (
    SCHOOL_YEARS.find((y) => y.value === value) ?? {
      value,
      label: value.replace(/^(\d{4})-(\d{2})(\d{2})$/, '$1–$3'),
      status: 'closed',
    }
  )
}
